/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { formatIsoDate, parseIsoDate } from '@shared/vueltoDateTime';
import { plural } from '../../shared/format/plural';
import { formatMoney } from '../../shared/format/public-api';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField'
import { showSuccess, showError } from '../../shared/ui/appToast'
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import { TicketButton } from '../../shared/ticket/TicketButton'
import { TicketSheet } from '../../shared/ticket/TicketSheet'
import { ticketControls } from '../../shared/ticket/ticketControls.styles'
import { ticketText } from '../../shared/ticket/ticketText'
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen';
import {
  listCommissionSummaries,
  listCommissionEarnings,
  listCommissionSettlements,
  listCommissionPlans,
  listCommissionAssignments,
  createCommissionSettlement,
  approveCommissionSettlement,
  payCommissionSettlement,
  cancelCommissionSettlement,
  voidCommissionSettlement,
  createCommissionAdjustment,
  listCommissionAdjustments,
  voidCommissionAdjustment,
  registerAdvance,
  registerProductWithdrawal,
} from '../infra/commissionsRepo';
import {
  exportCommissionsSummaryToCsv,
  exportCommissionsEarningsToCsv,
  exportSettlementsToCsv,
} from '../infra/commissionCsvExport';
import { commissionErrorMessage, breadcrumbCommission } from '../application-api';
import { SettlementReasonSheet } from '../components/SettlementReasonSheet';
import { buildCsvFromRows } from '../../shared/csv-core/infra/buildCsv'
import { shareOrDownloadCsv } from '../../shared/csv-core/infra/shareCsv';
import { listCatalogProducts } from '../../catalog/infra/catalogReadRepo';
import type { CatalogProductRow } from '../../catalog/domain-api';
import type {
  CommissionSummary,
  CommissionEarning,
  CommissionSettlement,
  CommissionPlan,
  CommissionAssignment,
  CommissionAdjustment,
  AdjustmentType,
  PaymentMethod,
} from '../domain/commissionTypes';
import {
  ADJUSTMENT_TYPE_LABELS,
  SETTLEMENT_STATUS_LABELS,
  EARNING_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  commissionEarningTitle,
  dedupeKpiValues,
  isNoPlanEarning,
} from '../domain/commissionTypes';
import { SaleDetailModal } from '../../sales/components/SaleDetailModal';
import { getMergedActiveCashSessionId } from '../../cash/infra-api';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { useContentLayout } from '../../shared/layout/useContentLayout';

type Tab = 'summary' | 'earnings' | 'settlements' | 'adjustments';
type DatePreset = 'this_month' | 'last_month' | 'all' | 'custom';

const PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'this_month', label: 'Este mes' },
  { key: 'last_month', label: 'Mes anterior' },
  { key: 'all', label: 'Todo' },
];

const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Resumen' },
  { key: 'earnings', label: 'Comisiones' },
  { key: 'settlements', label: 'Liquidaciones' },
  { key: 'adjustments', label: 'Ajustes' },
];


function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return iso;
  }
}

function presetToRange(preset: DatePreset): { from: Date | null; to: Date | null } {
  const now = new Date();
  if (preset === 'this_month') {
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1),
      to: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
    };
  }
  if (preset === 'last_month') {
    return {
      from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      to: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
    };
  }
  return { from: null, to: null };
}

const EARNING_STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'muted'> = {
  pending: 'warn',
  settled: 'ok',
  voided: 'bad',
};

const SETTLEMENT_STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'muted'> = {
  draft: 'muted',
  approved: 'ok',
  paid: 'ok',
  voided: 'bad',
};

type CommissionsNav = {
  navigate: (
    screen: 'CommissionPlans' | 'BoothRentals' | 'SettlementDetail',
    params?: { settlementId?: string },
  ) => void;
};

export function CommissionsScreen() {
  const navigation = useNavigation<CommissionsNav>();
  const { layoutWide } = useContentLayout('dataWide');
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const { canEdit, canVoid, canViewOthers, actions } = useBranchPermission('Commissions');
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;
  const organizationId = entitlements?.organization_id ?? null;
  const sessionUserId = useAuthStore((s) => s.session?.user?.id ?? null);

  const canSettle = isOwner || actions.settle === true || canEdit;
  const canApprove = isOwner || actions.approve === true || canEdit;
  const canPay = isOwner || actions.pay === true || canEdit;
  const canVoidSettlement = isOwner || actions.void === true || canVoid;
  const canManageAdjustments = isOwner || actions.manage_adjustments === true || canEdit;
  const canManagePlans = isOwner || actions.manage_plans === true || canEdit;
  const capabilityGate = useCapabilityGate('commissions', {
    title: 'Comisiones no disponibles',
    subtitle: 'Este rubro no tiene comisiones.',
  });

  const { confirm, ConfirmModal } = useOperationalConfirm();

  const [tab, setTab] = useState<Tab>('summary');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailSaleId, setDetailSaleId] = useState<string | null>(null);

  const [preset, setPreset] = useState<DatePreset>('this_month');
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);

  const [summaries, setSummaries] = useState<CommissionSummary[]>([]);
  const [earnings, setEarnings] = useState<CommissionEarning[]>([]);
  const [settlements, setSettlements] = useState<CommissionSettlement[]>([]);
  const [adjustments, setAdjustments] = useState<CommissionAdjustment[]>([]);
  const [plans, setPlans] = useState<CommissionPlan[]>([]);
  const [assignments, setAssignments] = useState<CommissionAssignment[]>([]);

  const [settlementModal, setSettlementModal] = useState<{
    visible: boolean;
    employeeUserId?: string;
    employeeName?: string;
  }>({ visible: false });
  const [adjustmentModal, setAdjustmentModal] = useState<{
    visible: boolean;
    employeeUserId?: string;
    employeeName?: string;
  }>({ visible: false });
  const [payModal, setPayModal] = useState<{
    visible: boolean;
    settlementId?: string;
  }>({ visible: false });
  const [reasonModal, setReasonModal] = useState<{
    visible: boolean;
    mode: 'void' | 'cancel';
    settlementId: string;
  } | null>(null);
  const [settleBusy, setSettleBusy] = useState(false);

  const range = useMemo(() => {
    if (preset === 'custom') return { from: customFrom, to: customTo };
    return presetToRange(preset);
  }, [preset, customFrom, customTo]);

  const fromIso = range.from ? range.from.toISOString() : null;
  const toIso = range.to ? range.to.toISOString() : null;

  const load = useCallback(async (isRefresh = false) => {
    if (!currentBranchId) {
      setError('No hay sucursal seleccionada.');
      setLoading(false);
      return;
    }
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const employeeFilter = !canViewOthers && !isOwner && sessionUserId ? sessionUserId : null;
      const [sums, earns, sets, adjs, plns, asgns] = await Promise.all([
        listCommissionSummaries({ branchId: currentBranchId, fromIso, toIso }),
        listCommissionEarnings({ branchId: currentBranchId, fromIso, toIso, employeeUserId: employeeFilter }),
        listCommissionSettlements({ branchId: currentBranchId, fromIso, toIso, employeeUserId: employeeFilter }),
        listCommissionAdjustments({ branchId: currentBranchId, fromIso, toIso, employeeUserId: employeeFilter }),
        listCommissionPlans(currentBranchId),
        listCommissionAssignments(null),
      ]);
      // Filtrar summaries client-side si canViewOthers es false
      const filteredSums = employeeFilter
        ? sums.filter((s) => s.employeeUserId === employeeFilter)
        : sums;
      setSummaries(filteredSums);
      setEarnings(earns);
      setSettlements(sets);
      setAdjustments(adjs);
      setPlans(plns);
      const planIds = new Set(plns.map((p) => p.id));
      setAssignments(asgns.filter((a) => planIds.has(a.planId)));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar comisiones.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentBranchId, fromIso, toIso, canViewOthers, isOwner, sessionUserId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const grandTotal = summaries.reduce((sum, s) => sum + s.grossEarnings, 0);
  const pendingTotal = summaries.reduce((sum, s) => sum + s.pendingEarnings, 0);
  const netTotal = summaries.reduce((sum, s) => sum + s.netPayable, 0);

  const handleCreateSettlement = useCallback(async (employeeUserId: string, employeeName: string) => {
    if (settleBusy) return;
    if (!range.from || !range.to) {
      showError('Error', 'Seleccioná un período personalizado para liquidar.');
      return;
    }
    setSettleBusy(true);
    breadcrumbCommission('settlement_create', 'create_commission_settlement invoked', {
      employeeUserId,
      branchId: currentBranchId ?? null,
    });
    try {
      const result = await createCommissionSettlement({
        employeeUserId,
        periodFrom: range.from.toISOString(),
        periodTo: range.to.toISOString(),
        branchId: currentBranchId ?? null,
      });
      if (result.ok) {
        showSuccess('Liquidación creada', `${employeeName}: ${formatMoney(result.net)} neto`);
        setSettlementModal({ visible: false });
        void load(true);
      } else {
        showError('Error', commissionErrorMessage(result.error, 'No se pudo crear la liquidación.'));
      }
    } catch (e: unknown) {
      showError('Error', commissionErrorMessage(e instanceof Error ? e.message : null, 'Error al crear liquidación.'));
    } finally {
      setSettleBusy(false);
    }
  }, [settleBusy, range, currentBranchId, load]);

  const handleApprove = useCallback(async (settlementId: string) => {
    if (settleBusy) return;
    const ok = await confirm({
      title: 'Aprobar liquidación',
      message: '¿Seguro que querés aprobar esta liquidación?',
      confirmLabel: 'Aprobar',
    });
    if (!ok) return;
    setSettleBusy(true);
    breadcrumbCommission('settlement_approve', 'approve_commission_settlement invoked', { settlementId });
    try {
      const result = await approveCommissionSettlement(settlementId);
      if (result.ok) {
        showSuccess('Aprobada', 'Liquidación aprobada');
        void load(true);
      } else {
        showError('Error', commissionErrorMessage(result.error, 'No se pudo aprobar.'));
      }
    } catch (e: unknown) {
      showError('Error', commissionErrorMessage(e instanceof Error ? e.message : null, 'Error al aprobar.'));
    } finally {
      setSettleBusy(false);
    }
  }, [settleBusy, confirm, load]);

  const handlePay = useCallback(async (
    settlementId: string,
    method: PaymentMethod,
    paymentRef?: string | null,
    note?: string | null,
  ) => {
    if (settleBusy) return;
    const ok = await confirm({
      title: 'Pagar liquidación',
      message: '¿Seguro que querés marcar esta liquidación como pagada?',
      confirmLabel: 'Pagar',
    });
    if (!ok) return;
    setSettleBusy(true);
    breadcrumbCommission('settlement_pay', 'pay_commission_settlement invoked', {
      settlementId,
      paymentMethod: method,
    });
    try {
      let cashSessionId: string | null = null;
      if (method === 'cash' && currentBranchId) {
        cashSessionId = await getMergedActiveCashSessionId(currentBranchId);
        if (!cashSessionId) {
          showError(
            'Sin caja abierta',
            'Para pagar en efectivo, abrí una caja en la sucursal actual primero.',
          );
          return;
        }
      }
      const result = await payCommissionSettlement({
        settlementId,
        paymentMethod: method,
        paymentRef: paymentRef ?? null,
        note: note ?? null,
        cashSessionId,
      });
      if (result.ok) {
        showSuccess('Pagada', 'Liquidación marcada como pagada');
        setPayModal({ visible: false });
        void load(true);
      } else {
        showError('Error', commissionErrorMessage(result.error, 'No se pudo pagar.'));
      }
    } catch (e: unknown) {
      showError('Error', commissionErrorMessage(e instanceof Error ? e.message : null, 'Error al pagar.'));
    } finally {
      setSettleBusy(false);
    }
  }, [settleBusy, confirm, load, currentBranchId]);

  const handleVoid = useCallback(async (settlementId: string) => {
    if (settleBusy) return;
    setReasonModal({ visible: true, mode: 'void', settlementId });
  }, [settleBusy]);

  const handleCancel = useCallback(async (settlementId: string) => {
    if (settleBusy) return;
    setReasonModal({ visible: true, mode: 'cancel', settlementId });
  }, [settleBusy]);

  const handleReasonConfirm = useCallback(async (reason: string) => {
    if (!reasonModal || settleBusy) return;
    const { mode, settlementId } = reasonModal;
    setSettleBusy(true);
    breadcrumbCommission(
      mode === 'void' ? 'settlement_void' : 'settlement_cancel',
      mode === 'void' ? 'void_commission_settlement invoked' : 'cancel_commission_settlement invoked',
      { settlementId },
    );
    try {
      const result = mode === 'void'
        ? await voidCommissionSettlement(settlementId, reason)
        : await cancelCommissionSettlement(settlementId, reason);
      if (result.ok) {
        showSuccess(mode === 'void' ? 'Anulada' : 'Descartada', mode === 'void' ? 'Liquidación anulada' : 'Liquidación descartada');
        setReasonModal(null);
        void load(true);
      } else {
        showError(
          'Error',
          commissionErrorMessage(result.error, mode === 'void' ? 'No se pudo anular.' : 'No se pudo descartar.'),
        );
      }
    } catch (e: unknown) {
      showError(
        'Error',
        commissionErrorMessage(
          e instanceof Error ? e.message : null,
          mode === 'void' ? 'Error al anular.' : 'Error al descartar.',
        ),
      );
    } finally {
      setSettleBusy(false);
    }
  }, [reasonModal, settleBusy, load]);

  const handleVoidAdjustment = useCallback(async (adjustmentId: string) => {
    if (settleBusy) return;
    const ok = await confirm({
      title: 'Anular ajuste',
      message: '¿Seguro que querés anular este ajuste?',
      confirmLabel: 'Anular',
      destructive: true,
    });
    if (!ok) return;
    setSettleBusy(true);
    try {
      await voidCommissionAdjustment(adjustmentId, 'Anulado por dueño');
      showSuccess('Anulado', 'Ajuste anulado');
      void load(true);
    } catch (e: unknown) {
      showError('Error', e instanceof Error ? e.message : 'Error al anular ajuste.');
    } finally {
      setSettleBusy(false);
    }
  }, [settleBusy, confirm, load]);

  const handleExport = useCallback(async () => {
    try {
      if (tab === 'summary') {
        await exportCommissionsSummaryToCsv(summaries);
        showSuccess('Exportado', 'Resumen exportado a CSV');
      } else if (tab === 'earnings') {
        await exportCommissionsEarningsToCsv(earnings);
        showSuccess('Exportado', 'Detalle exportado a CSV');
      } else if (tab === 'settlements') {
        await exportSettlementsToCsv(settlements);
        showSuccess('Exportado', 'Liquidaciones exportadas a CSV');
      } else if (tab === 'adjustments') {
        const headers = ['Empleado', 'Tipo', 'Motivo', 'Monto', 'Estado', 'Fecha'];
        const rows = adjustments.map((a) => [
          a.employeeName,
          ADJUSTMENT_TYPE_LABELS[a.adjustmentType],
          a.reason,
          a.amount.toFixed(2),
          a.status,
          formatDate(a.effectiveDate),
        ]);
        const csv = buildCsvFromRows(headers, rows);
        await shareOrDownloadCsv(csv, 'ajustes.csv');
        showSuccess('Exportado', 'Ajustes exportados a CSV');
      }
    } catch (e: unknown) {
      showError('Error', e instanceof Error ? e.message : 'Error al exportar.');
    }
  }, [tab, summaries, earnings, settlements, adjustments]);

  // ---- Loading / Error states ----
  if (loading) {
    return (
      <OperationalScrollScreen
        header={null}
        body={
          <View style={cStyles.center}>
            <ActivityIndicator size="large" color={ticketColors.gold} />
          </View>
        }
      />
    );
  }

  if (error) {
    return (
      <OperationalScrollScreen
        header={null}
        body={
          <ModuleEmptyState
            variant="nest"
            kicker="Comisiones"
            title="No se pudieron cargar las comisiones"
            subtitle={error}
            primaryAction={{ label: 'Reintentar', onPress: () => void load() }}
          />
        }
      />
    );
  }

  // #1454: si dos KPIs dicen el mismo número no informan — queda el más accionable.
  const kpiItems: TicketKpiItem[] = dedupeKpiValues([
    { label: 'Total comisiones', value: formatMoney(grandTotal) },
    { label: 'Pendiente', value: formatMoney(pendingTotal), tone: pendingTotal > 0 ? 'warn' : 'default' },
    { label: 'A liquidar', value: formatMoney(netTotal), tone: 'ok' as const },
  ] as TicketKpiItem[]);

  // Planes y Alquileres ya son destinos del sidebar — no duplicar accesos acá.
  const headerActions = (
    <View style={[cStyles.headerActions, !layoutWide && cStyles.headerActionsMobile]}>
      <TicketButton label="Exportar" variant="ghost" testID="commissions.export" onPress={() => void handleExport()} />
    </View>
  );

  const header = (
    <TicketModuleHeader
      title="Comisiones"
      meta={`${plans.length} ${plural(plans.length, 'plan', 'planes')} · ${assignments.length} ${plural(assignments.length, 'asignación', 'asignaciones')}`}
      trailing={layoutWide ? headerActions : null}
    >
      {!layoutWide ? headerActions : null}
      {/* Date presets */}
      <View style={cStyles.presetRow}>
        {PRESETS.map((p) => (
          <TouchableOpacity
            key={p.key}
            style={[cStyles.presetBtn, preset === p.key && cStyles.presetBtnActive]}
            onPress={() => { setPreset(p.key); setCustomFrom(null); setCustomTo(null); }}
          >
            <Text style={[cStyles.presetText, preset === p.key && cStyles.presetTextActive]}>{p.label}</Text>
          </TouchableOpacity>
        ))}
        <TouchableOpacity
          style={[cStyles.presetBtn, preset === 'custom' && cStyles.presetBtnActive]}
          onPress={() => setPreset('custom')}
        >
          <Text style={[cStyles.presetText, preset === 'custom' && cStyles.presetTextActive]}>Personalizado</Text>
        </TouchableOpacity>
      </View>
      {preset === 'custom' ? (
        <View style={cStyles.customDateRow}>
          <View style={cStyles.customDateField}>
            <Text style={cStyles.customDateLabel}>Desde</Text>
            <InventoryIsoDateField
              value={customFrom ? formatIsoDate(customFrom) : ''}
              onChange={(ymd) => {
                if (!ymd) { setCustomFrom(null); return; }
                const d = parseIsoDate(ymd); d.setHours(0, 0, 0, 0); setCustomFrom(d);
              }}
              emptyLabel="Elegí fecha"
              ariaLabel="Fecha desde"
              showClearButton={false}
              iosEmptyBehavior="openPicker"
            />
          </View>
          <View style={cStyles.customDateField}>
            <Text style={cStyles.customDateLabel}>Hasta</Text>
            <InventoryIsoDateField
              value={customTo ? formatIsoDate(customTo) : ''}
              onChange={(ymd) => {
                if (!ymd) { setCustomTo(null); return; }
                const d = parseIsoDate(ymd); d.setHours(23, 59, 59, 999); setCustomTo(d);
              }}
              emptyLabel="Elegí fecha"
              ariaLabel="Fecha hasta"
              showClearButton={false}
              iosEmptyBehavior="openPicker"
            />
          </View>
          <TicketButton label="Aplicar" variant="gold" onPress={() => void load()} style={cStyles.applyBtn} />
        </View>
      ) : null}
    </TicketModuleHeader>
  );

  const body = (
    <>
      {/* KPIs */}
      <PaperSheet>
        <TicketKpiStrip items={kpiItems} variant="soft" layout={layoutWide ? 'desk' : 'mobile'} />
      </PaperSheet>

      {/* Tabs */}
      <PaperSheet style={cStyles.tabSheet}>
        <View style={cStyles.tabRow}>
          {TABS.map((t) => (
            <TouchableOpacity
              key={t.key}
              style={[cStyles.tabBtn, tab === t.key && cStyles.tabBtnActive]}
              onPress={() => setTab(t.key)}
              testID={`commissions.tab.${t.key}`}
              accessibilityRole="tab"
              accessibilityState={{ selected: tab === t.key }}
            >
              <Text style={[cStyles.tabText, tab === t.key && cStyles.tabTextActive]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </PaperSheet>

      {/* Tab content */}
      {tab === 'summary' ? (
        <SummaryTab
          summaries={summaries}
          canSettle={canSettle}
          canManageAdjustments={canManageAdjustments}
          onLiquidate={(emp) => setSettlementModal({ visible: true, employeeUserId: emp.employeeUserId, employeeName: emp.employeeName })}
          onAdjustment={(emp) => setAdjustmentModal({ visible: true, employeeUserId: emp.employeeUserId, employeeName: emp.employeeName })}
        />
      ) : null}

      {tab === 'earnings' ? (
        <EarningsTab
          earnings={earnings}
          onViewSale={(saleId) => setDetailSaleId(saleId)}
          onOpenPlans={canManagePlans ? () => navigation.navigate('CommissionPlans') : undefined}
        />
      ) : null}

      {tab === 'settlements' ? (
        <SettlementsTab
          settlements={settlements}
          canApprove={canApprove}
          canPay={canPay}
          canVoid={canVoidSettlement}
          canSettle={canSettle}
          onApprove={handleApprove}
          onPay={(id) => setPayModal({ visible: true, settlementId: id })}
          onVoid={handleVoid}
          onCancel={handleCancel}
          onOpenDetail={(id) => navigation.navigate('SettlementDetail', { settlementId: id })}
        />
      ) : null}

      {tab === 'adjustments' ? (
        <AdjustmentsTab
          adjustments={adjustments}
          canManageAdjustments={canManageAdjustments}
          onVoidAdjustment={handleVoidAdjustment}
          onAddAdjustment={() => setAdjustmentModal({ visible: true })}
        />
      ) : null}
    </>
  );

  const modals = (
    <>
      <SettlementModal
        visible={settlementModal.visible}
        employeeName={settlementModal.employeeName ?? ''}
        periodFrom={range.from}
        periodTo={range.to}
        busy={settleBusy}
        onClose={() => setSettlementModal({ visible: false })}
        onConfirm={() => {
          if (settlementModal.employeeUserId) {
            void handleCreateSettlement(settlementModal.employeeUserId, settlementModal.employeeName ?? '');
          }
        }}
      />
      <AdjustmentModal
        visible={adjustmentModal.visible}
        employeeName={adjustmentModal.employeeName ?? ''}
        organizationId={organizationId ?? ''}
        branchId={currentBranchId ?? null}
        employeeUserId={adjustmentModal.employeeUserId ?? ''}
        onClose={() => setAdjustmentModal({ visible: false })}
        onCreated={() => { setAdjustmentModal({ visible: false }); void load(true); }}
      />
      <PayModal
        visible={payModal.visible}
        onClose={() => setPayModal({ visible: false })}
        onPay={(method, paymentRef, note) => {
          if (payModal.settlementId) void handlePay(payModal.settlementId, method, paymentRef, note);
        }}
      />
      <SettlementReasonSheet
        visible={reasonModal?.visible === true}
        title={reasonModal?.mode === 'void' ? 'Anular liquidación' : 'Descartar liquidación'}
        subtitle={
          reasonModal?.mode === 'void'
            ? 'Las comisiones vuelven a pendientes.'
            : 'Las comisiones vuelven a pendientes para liquidar de nuevo.'
        }
        confirmLabel={reasonModal?.mode === 'void' ? 'Anular' : 'Descartar'}
        defaultReason={reasonModal?.mode === 'void' ? 'Anulada por dueño' : 'Descartada por dueño'}
        busy={settleBusy}
        onClose={() => setReasonModal(null)}
        onConfirm={(reason) => void handleReasonConfirm(reason)}
      />
      <SaleDetailModal
        visible={detailSaleId != null}
        saleId={detailSaleId}
        branchId={currentBranchId ?? null}
        onClose={() => setDetailSaleId(null)}
      />
      {ConfirmModal}
    </>
  );

  if (!capabilityGate.allowed) {
    return <OperationalScrollScreen body={capabilityGate.blocked} />;
  }

  return (
    <OperationalScrollScreen
      header={header}
      body={body}
      modals={modals}
      refreshing={refreshing}
      onRefresh={() => void load(true)}
    />
  );
}

// ============================================================================
// Summary Tab
// ============================================================================
function SummaryTab({ summaries, canSettle, canManageAdjustments, onLiquidate, onAdjustment }: {
  summaries: CommissionSummary[];
  canSettle: boolean;
  canManageAdjustments: boolean;
  onLiquidate: (emp: CommissionSummary) => void;
  onAdjustment: (emp: CommissionSummary) => void;
}) {
  if (summaries.length === 0) {
    return (
      <ModuleEmptyState
        variant="nest"
        kicker="Comisiones"
        title="Sin comisiones en este período"
        subtitle="No hay comisiones generadas para la sucursal y período seleccionados."
      />
    );
  }
  return (
    <PaperSheet>
      {summaries.map((s, idx) => (
        <TicketListRow
          key={s.employeeUserId}
          title={s.employeeName}
          subtitle={`${s.earningsCount} ${plural(s.earningsCount, 'comisión', 'comisiones')} · Pendiente: ${formatMoney(s.pendingEarnings)}`}
          meta={`Ajustes: ${formatMoney(s.adjustmentsTotal)}`}
          value={formatMoney(s.netPayable)}
          statusLabel="a liquidar"
          statusTone="ok"
          group={idx === summaries.length - 1 ? 'groupedLast' : 'grouped'}
          footer={
            (canSettle || canManageAdjustments) ? (
              <View style={cStyles.cardActions}>
                {canManageAdjustments ? (
                  <TicketButton label="+ Ajuste" variant="ghost" onPress={() => onAdjustment(s)} />
                ) : null}
                {canSettle ? (
                  <TicketButton
                    label="Liquidar"
                    variant="gold"
                    testID={`commissions.summary.liquidate.${s.employeeUserId}`}
                    onPress={() => onLiquidate(s)}
                    disabled={s.pendingEarnings === 0 && s.adjustmentsTotal === 0}
                  />
                ) : null}
              </View>
            ) : null
          }
        />
      ))}
    </PaperSheet>
  );
}

// ============================================================================
// Earnings Tab
// ============================================================================
function EarningsTab({ earnings, onViewSale, onOpenPlans }: {
  earnings: CommissionEarning[];
  onViewSale?: (saleId: string) => void;
  onOpenPlans?: () => void;
}) {
  if (earnings.length === 0) {
    return (
      <ModuleEmptyState
        variant="nest"
        kicker="Comisiones"
        title="Sin comisiones generadas"
        subtitle="No hay comisiones generadas en este período."
      />
    );
  }
  // «Sin plan asignado» va una sola vez en banner, no repetido por fila.
  const noPlanEmployees = new Set(
    earnings.filter(isNoPlanEarning).map((e) => e.employeeUserId),
  );
  return (
    <PaperSheet>
      {noPlanEmployees.size > 0 ? (
        <View style={earningsTabStyles.noPlanHint}>
          <Text style={earningsTabStyles.noPlanHintText}>
            {noPlanEmployees.size}{' '}
            {plural(noPlanEmployees.size, 'empleado', 'empleados')} sin plan de
            comisión asignado — esas ventas no suman comisión.
            {onOpenPlans ? (
              <Text style={earningsTabStyles.noPlanHintLink} onPress={onOpenPlans}>
                {' '}Asignar plan →
              </Text>
            ) : null}
          </Text>
        </View>
      ) : null}
      {earnings.map((e, idx) => {
        const hasSaleLink = e.sourceType === 'sale_item' && e.sourceRef && onViewSale;
        const noPlan = isNoPlanEarning(e);
        return (
          <TicketListRow
            key={e.id}
            title={e.employeeName}
            subtitle={noPlan ? null : commissionEarningTitle(e.description)}
            meta={`Base: ${formatMoney(e.commissionableAmount)} · ${formatDate(e.earnedAt)}`}
            value={formatMoney(e.commissionAmount)}
            statusLabel={noPlan ? 'Sin plan' : EARNING_STATUS_LABELS[e.status]}
            statusTone={noPlan ? 'muted' : EARNING_STATUS_TONE[e.status] ?? 'muted'}
            chipLabel={e.ratePercent != null ? `${e.ratePercent}%` : undefined}
            chipTone="default"
            group={idx === earnings.length - 1 ? 'groupedLast' : 'grouped'}
            onPress={hasSaleLink ? () => onViewSale(e.sourceRef!) : undefined}
            showChevron={Boolean(hasSaleLink)}
            accessibilityLabel={hasSaleLink ? `Ver venta de ${e.employeeName}` : undefined}
          />
        );
      })}
    </PaperSheet>
  );
}

// ============================================================================
// Settlements Tab
// ============================================================================
function SettlementsTab({ settlements, canApprove, canPay, canVoid, canSettle, onApprove, onPay, onVoid, onCancel, onOpenDetail }: {
  settlements: CommissionSettlement[];
  canApprove: boolean;
  canPay: boolean;
  canVoid: boolean;
  canSettle: boolean;
  onApprove: (id: string) => void;
  onPay: (id: string) => void;
  onVoid: (id: string) => void;
  onCancel: (id: string) => void;
  onOpenDetail: (id: string) => void;
}) {
  if (settlements.length === 0) {
    return (
      <ModuleEmptyState
        variant="nest"
        kicker="Comisiones"
        title="Sin liquidaciones"
        subtitle="No hay liquidaciones en este período."
      />
    );
  }
  return (
    <PaperSheet>
      {settlements.map((s, idx) => (
        <TicketListRow
          key={s.id}
          testID={`commissions.settlement.row.${s.id}`}
          title={s.employeeName}
          subtitle={`${formatDate(s.periodFrom)} → ${formatDate(s.periodTo)}`}
          meta={`Bruto: ${formatMoney(s.grossEarnings)} · Ajustes: ${formatMoney(s.adjustmentsTotal)}`}
          value={formatMoney(s.netPayable)}
          statusLabel={SETTLEMENT_STATUS_LABELS[s.status]}
          statusTone={SETTLEMENT_STATUS_TONE[s.status] ?? 'muted'}
          chipLabel={s.paymentMethod ? PAYMENT_METHOD_LABELS[s.paymentMethod] : undefined}
          chipTone="default"
          group={idx === settlements.length - 1 ? 'groupedLast' : 'grouped'}
          onPress={() => onOpenDetail(s.id)}
          footer={
            <View style={cStyles.cardActions}>
              {canApprove && s.status === 'draft' ? (
                <TicketButton
                  label="Aprobar"
                  variant="gold"
                  testID={`commissions.settlement.approve.${s.id}`}
                  onPress={() => onApprove(s.id)}
                />
              ) : null}
              {canPay && s.status === 'approved' ? (
                <TicketButton
                  label="Pagar"
                  variant="gold"
                  testID={`commissions.settlement.pay.${s.id}`}
                  onPress={() => onPay(s.id)}
                />
              ) : null}
              {canSettle && (s.status === 'draft' || s.status === 'approved') ? (
                <TicketButton
                  label="Descartar"
                  variant="danger"
                  testID={`commissions.settlement.discard.${s.id}`}
                  onPress={() => void onCancel(s.id)}
                />
              ) : null}
              {canVoid && s.status === 'paid' ? (
                <TicketButton
                  label="Anular"
                  variant="danger"
                  testID={`commissions.settlement.void.${s.id}`}
                  onPress={() => void onVoid(s.id)}
                />
              ) : null}
            </View>
          }
        />
      ))}
    </PaperSheet>
  );
}

// ============================================================================
// Adjustments Tab
// ============================================================================
function AdjustmentsTab({ adjustments, canManageAdjustments, onVoidAdjustment, onAddAdjustment }: {
  adjustments: CommissionAdjustment[];
  canManageAdjustments: boolean;
  onVoidAdjustment: (id: string) => void;
  onAddAdjustment: () => void;
}) {
  if (adjustments.length === 0) {
    return (
      <PaperSheet>
        {canManageAdjustments ? (
          <View style={cStyles.cardActions}>
            <TicketButton label="+ Ajuste" variant="gold" onPress={onAddAdjustment} />
          </View>
        ) : null}
        <ModuleEmptyState
          variant="nest"
          kicker="Comisiones"
          title="Sin ajustes"
          subtitle="No hay ajustes en este período."
        />
      </PaperSheet>
    );
  }
  return (
    <PaperSheet>
      {canManageAdjustments ? (
        <View style={cStyles.cardActions}>
          <TicketButton label="+ Ajuste" variant="gold" onPress={onAddAdjustment} />
        </View>
      ) : null}
      {adjustments.map((a, idx) => {
        const isWithdrawal = a.adjustmentType === 'product_withdrawal';
        const withdrawalDetail = isWithdrawal && a.quantity != null && a.unitValue != null
          ? `${a.quantity}u × ${formatMoney(a.unitValue)}`
          : null;
        const canVoid = canManageAdjustments && (a.status === 'pending' || a.status === 'settled');
        return (
          <TicketListRow
            key={a.id}
            title={a.employeeName}
            subtitle={`${ADJUSTMENT_TYPE_LABELS[a.adjustmentType]} · ${formatDate(a.effectiveDate)}`}
            meta={withdrawalDetail ? `${withdrawalDetail} — ${a.reason}` : a.reason}
            value={`${a.amount >= 0 ? '+' : ''}${formatMoney(a.amount)}`}
            statusLabel={a.status === 'pending' ? 'Pendiente' : a.status === 'settled' ? 'Liquidado' : 'Anulado'}
            statusTone={a.status === 'pending' ? 'warn' : a.status === 'settled' ? 'ok' : 'bad'}
            group={idx === adjustments.length - 1 ? 'groupedLast' : 'grouped'}
            footer={canVoid ? (
              <View style={cStyles.cardActions}>
                <TicketButton label="Anular" variant="danger" onPress={() => void onVoidAdjustment(a.id)} />
              </View>
            ) : undefined}
          />
        );
      })}
    </PaperSheet>
  );
}

// ============================================================================
// Settlement Modal
// ============================================================================
function SettlementModal({ visible, employeeName, periodFrom, periodTo, busy, onClose, onConfirm }: {
  visible: boolean;
  employeeName: string;
  periodFrom: Date | null;
  periodTo: Date | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <TicketSheet
      visible={visible}
      title="Crear liquidación"
      subtitle={`Empleado: ${employeeName}`}
      onClose={onClose}
      fitContent
      footer={
        <View style={cStyles.modalActions}>
          <TicketButton label="Cancelar" variant="ghost" onPress={onClose} style={{ flex: 1 }} />
          <TicketButton
            label={busy ? 'Creando…' : 'Confirmar'}
            variant="gold"
            testID="commissions.settlement.create.confirm"
            onPress={onConfirm}
            disabled={busy}
            style={{ flex: 1 }}
          />
        </View>
      }
    >
      <View style={cStyles.modalBody}>
        <Text style={ticketText.body}>
          Período: {periodFrom ? formatDate(periodFrom.toISOString()) : '—'} → {periodTo ? formatDate(periodTo.toISOString()) : '—'}
        </Text>
        <Text style={cStyles.modalWarning}>
          Se incluirán todas las comisiones pendientes y ajustes del período.
        </Text>
      </View>
    </TicketSheet>
  );
}

// ============================================================================
// Adjustment Modal
// ============================================================================
function AdjustmentModal({ visible, employeeName, organizationId, branchId, employeeUserId, onClose, onCreated }: {
  visible: boolean;
  employeeName: string;
  organizationId: string;
  branchId: string | null;
  employeeUserId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('advance');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitValue, setUnitValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<CatalogProductRow[]>([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [productLoading, setProductLoading] = useState(false);

  const isProductWithdrawal = adjustmentType === 'product_withdrawal';

  // Search products from catalog when picker is open
  useEffect(() => {
    if (!isProductWithdrawal || !productPickerOpen) return;
    let cancelled = false;
    setProductLoading(true);
    void listCatalogProducts({ search: productSearch, itemType: 'physical', limit: 20 })
      .then((rows) => {
        if (!cancelled) setProductResults(rows);
      })
      .catch(() => {
        if (!cancelled) setProductResults([]);
      })
      .finally(() => {
        if (!cancelled) setProductLoading(false);
      });
    return () => { cancelled = true; };
  }, [productSearch, productPickerOpen, isProductWithdrawal]);

  const handleSave = useCallback(async () => {
    if (!reason.trim()) {
      showError('Error', 'Ingresá un motivo.');
      return;
    }
    setSaving(true);
    const operationId = crypto.randomUUID();
    try {
      if (isProductWithdrawal) {
        const qty = parseFloat(quantity);
        const unitVal = parseFloat(unitValue);
        if (!productId.trim()) {
          showError('Error', 'Seleccioná un producto del catálogo.');
          setSaving(false);
          return;
        }
        if (isNaN(qty) || qty <= 0) {
          showError('Error', 'Ingresá una cantidad válida.');
          setSaving(false);
          return;
        }
        if (isNaN(unitVal) || unitVal < 0) {
          showError('Error', 'Ingresá un valor unitario válido.');
          setSaving(false);
          return;
        }
        await registerProductWithdrawal({
          employeeUserId,
          productId: productId.trim(),
          quantity: qty,
          unitValue: unitVal,
          reason: reason.trim(),
          branchId,
          operationId,
        });
      } else {
        const amt = parseFloat(amount);
        if (isNaN(amt) || amt === 0) {
          showError('Error', 'Ingresá un monto válido.');
          setSaving(false);
          return;
        }
        if (adjustmentType === 'advance') {
          const result = await registerAdvance({
            employeeUserId,
            amount: amt,
            reason: reason.trim(),
            branchId,
            description: reason.trim(),
            operationId,
          });
          if (!result.ok) {
            showError('Error', commissionErrorMessage(result.error, 'No se pudo registrar el adelanto.'));
            setSaving(false);
            return;
          }
        } else {
          await createCommissionAdjustment({
            organizationId,
            branchId,
            employeeUserId,
            adjustmentType,
            amount: amt,
            reason: reason.trim(),
          });
        }
      }
      showSuccess('Ajuste creado', 'El ajuste se registró correctamente');
      setAmount(''); setReason(''); setAdjustmentType('advance');
      setProductId(''); setQuantity(''); setUnitValue('');
      setSelectedProductName(''); setProductSearch(''); setProductResults([]);
      onCreated();
    } catch (e: unknown) {
      showError('Error', e instanceof Error ? e.message : 'Error al crear ajuste.');
    } finally {
      setSaving(false);
    }
  }, [amount, reason, adjustmentType, organizationId, branchId, employeeUserId, onCreated, isProductWithdrawal, productId, quantity, unitValue, selectedProductName]);

  return (
    <TicketSheet
      visible={visible}
      title="Nuevo ajuste"
      subtitle={`Empleado: ${employeeName}`}
      onClose={onClose}
      footer={
        <View style={cStyles.modalActions}>
          <TicketButton label="Cancelar" variant="ghost" onPress={onClose} style={{ flex: 1 }} />
          <TicketButton label={saving ? 'Guardando…' : 'Guardar'} variant="gold" onPress={() => void handleSave()} disabled={saving} style={{ flex: 1 }} />
        </View>
      }
    >
      <ScrollView style={cStyles.modalBody} showsVerticalScrollIndicator={false}>
        <Text style={cStyles.fieldLabel}>Tipo</Text>
        <View style={cStyles.typeRow}>
          {(['advance', 'deduction', 'product_withdrawal', 'bonus', 'correction'] as AdjustmentType[]).map((t) => (
            <TouchableOpacity
              key={t}
              style={[cStyles.typeBtn, adjustmentType === t && cStyles.typeBtnActive]}
              onPress={() => setAdjustmentType(t)}
            >
              <Text style={[cStyles.typeBtnText, adjustmentType === t && cStyles.typeBtnTextActive]}>
                {ADJUSTMENT_TYPE_LABELS[t]}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {isProductWithdrawal ? (
          <>
            <Text style={cStyles.fieldLabel}>Producto</Text>
            {selectedProductName ? (
              <View style={cStyles.productSelectedRow}>
                <Text style={cStyles.productSelectedName}>{selectedProductName}</Text>
                <TicketButton
                  label="Cambiar"
                  variant="ghost"
                  onPress={() => {
                    setProductPickerOpen(true);
                    setSelectedProductName('');
                    setProductId('');
                  }}
                />
              </View>
            ) : (
              <TouchableOpacity
                style={ticketControls.input}
                onPress={() => setProductPickerOpen(true)}
              >
                <Text style={cStyles.pickerPlaceholder}>
                  {productLoading ? 'Buscando…' : 'Seleccionar producto'}
                </Text>
              </TouchableOpacity>
            )}

            {productPickerOpen ? (
              <View style={cStyles.pickerSheet}>
                <TextInput
                  style={ticketControls.input}
                  value={productSearch}
                  onChangeText={setProductSearch}
                  placeholder="Buscar por nombre o SKU…"
                  placeholderTextColor={ticketColors.ink3}
                  autoFocus
                />
                <ScrollView style={cStyles.pickerList} showsVerticalScrollIndicator={false}>
                  {productResults.length === 0 && !productLoading ? (
                    <Text style={cStyles.pickerEmpty}>Sin resultados</Text>
                  ) : null}
                  {productResults.map((p) => (
                    <TouchableOpacity
                      key={p.id}
                      style={cStyles.pickerItem}
                      onPress={() => {
                        setProductId(p.id);
                        setSelectedProductName(p.name);
                        setUnitValue(String(p.price));
                        setProductPickerOpen(false);
                      }}
                    >
                      <Text style={cStyles.pickerItemName}>{p.name}</Text>
                      <Text style={cStyles.pickerItemPrice}>
                        {formatMoney(p.price)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
                <TicketButton
                  label="Cancelar"
                  variant="ghost"
                  onPress={() => setProductPickerOpen(false)}
                />
              </View>
            ) : null}

            <Text style={cStyles.fieldLabel}>Cantidad</Text>
            <TextInput
              style={ticketControls.input}
              value={quantity}
              onChangeText={setQuantity}
              placeholder="0"
              placeholderTextColor={ticketColors.ink3}
              keyboardType="decimal-pad"
            />
            <Text style={cStyles.fieldLabel}>Valor unitario</Text>
            <TextInput
              style={ticketControls.input}
              value={unitValue}
              onChangeText={setUnitValue}
              placeholder="0.00"
              placeholderTextColor={ticketColors.ink3}
              keyboardType="decimal-pad"
            />
          </>
        ) : (
          <>
            <Text style={cStyles.fieldLabel}>Monto (negativo = descuento)</Text>
            <TextInput
              style={ticketControls.input}
              value={amount}
              onChangeText={setAmount}
              placeholder="0.00"
              placeholderTextColor={ticketColors.ink3}
              keyboardType="decimal-pad"
            />
          </>
        )}

        <Text style={cStyles.fieldLabel}>Motivo</Text>
        <TextInput
          style={ticketControls.input}
          value={reason}
          onChangeText={setReason}
          placeholder="Motivo del ajuste"
          placeholderTextColor={ticketColors.ink3}
        />
      </ScrollView>
    </TicketSheet>
  );
}

// ============================================================================
// Pay Modal
// ============================================================================
function PayModal({ visible, onClose, onPay }: {
  visible: boolean;
  onClose: () => void;
  onPay: (method: PaymentMethod, paymentRef?: string | null, note?: string | null) => void;
}) {
  const [paymentRef, setPaymentRef] = useState('');
  const [note, setNote] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);

  const handleSelect = (method: PaymentMethod) => {
    setSelectedMethod(method);
  };

  const handleConfirm = () => {
    if (!selectedMethod) return;
    onPay(selectedMethod, paymentRef.trim() || null, note.trim() || null);
    setPaymentRef(''); setNote(''); setSelectedMethod(null);
  };

  const handleClose = () => {
    setPaymentRef(''); setNote(''); setSelectedMethod(null);
    onClose();
  };

  return (
    <TicketSheet
      visible={visible}
      title="Pagar liquidación"
      subtitle="Seleccioná el método de pago"
      onClose={handleClose}
      footer={
        <View style={cStyles.modalActions}>
          <TicketButton label="Cancelar" variant="ghost" onPress={handleClose} style={{ flex: 1 }} />
          <TicketButton
            label="Confirmar pago"
            variant="gold"
            testID="commissions.settlement.pay.confirm"
            onPress={handleConfirm}
            disabled={!selectedMethod}
            style={{ flex: 1 }}
          />
        </View>
      }
    >
      <ScrollView style={cStyles.modalBody} showsVerticalScrollIndicator={false}>
        {(['cash', 'expense', 'bank_transfer', 'other'] as PaymentMethod[]).map((m) => (
          <TouchableOpacity
            key={m}
            style={[cStyles.payOption, selectedMethod === m && cStyles.payOptionActive]}
            onPress={() => handleSelect(m)}
            testID={`commissions.settlement.pay.method.${m}`}
          >
            <Text style={cStyles.payOptionText}>{PAYMENT_METHOD_LABELS[m]}</Text>
          </TouchableOpacity>
        ))}
        <Text style={cStyles.fieldLabel}>Referencia de pago (opcional)</Text>
        <TextInput
          style={ticketControls.input}
          value={paymentRef}
          onChangeText={setPaymentRef}
          placeholder="Ej. transferencia #12345"
          placeholderTextColor={ticketColors.ink3}
        />
        <Text style={cStyles.fieldLabel}>Nota (opcional)</Text>
        <TextInput
          style={ticketControls.input}
          value={note}
          onChangeText={setNote}
          placeholder="Nota interna"
          placeholderTextColor={ticketColors.ink3}
          multiline
        />
      </ScrollView>
    </TicketSheet>
  );
}

const cStyles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: spacing.xxl },
  headerActions: { flexDirection: 'row', gap: spacing.sm, alignItems: 'center' },
  headerActionsMobile: { flexWrap: 'wrap', marginTop: spacing.sm },
  presetRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm },
  presetBtn: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  presetBtnActive: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  presetText: { fontSize: 13, fontWeight: '500', color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  presetTextActive: { color: ticketColors.paper, fontWeight: '700' },
  customDateRow: { flexDirection: 'row', alignItems: 'flex-end', gap: spacing.sm, marginTop: spacing.sm, flexWrap: 'wrap' },
  customDateField: { flex: 1, minWidth: 120 },
  customDateLabel: { fontSize: 12, fontWeight: '600', color: ticketColors.ink2, marginBottom: 4, fontFamily: ticketFonts.sans },
  applyBtn: { paddingHorizontal: 16, paddingVertical: 10 },
  tabSheet: { padding: 4 },
  tabRow: { flexDirection: 'row', gap: 4, backgroundColor: ticketColors.tile, borderRadius: 10, padding: 4 },
  tabBtn: { flex: 1, paddingVertical: 10, borderRadius: 8, alignItems: 'center' },
  tabBtnActive: { backgroundColor: ticketColors.gold },
  tabText: { fontSize: 13, fontWeight: '500', color: ticketColors.ink3, fontFamily: ticketFonts.sans },
  tabTextActive: { color: ticketColors.paper, fontWeight: '700' },
  cardActions: { flexDirection: 'row', gap: spacing.sm, paddingTop: spacing.sm },
  modalBody: { padding: spacing.md },
  modalWarning: { fontSize: 13, color: ticketColors.ink2, marginTop: spacing.sm, fontStyle: 'italic', fontFamily: ticketFonts.sans },
  modalActions: { flexDirection: 'row', gap: spacing.sm, padding: spacing.md, paddingTop: 0 },
  fieldLabel: { fontSize: 13, fontWeight: '600', color: ticketColors.ink, marginTop: spacing.md, marginBottom: spacing.xs, fontFamily: ticketFonts.sans },
  typeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  typeBtn: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  typeBtnActive: { backgroundColor: ticketColors.gold, borderColor: ticketColors.gold },
  typeBtnText: { fontSize: 12, fontWeight: '500', color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  typeBtnTextActive: { color: ticketColors.paper, fontWeight: '700' },
  payOption: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 10,
    backgroundColor: 'transparent',
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  payOptionActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold + '20',
  },
  payOptionText: { fontSize: 14, fontWeight: '500', color: ticketColors.ink, fontFamily: ticketFonts.sans },
  pickerPlaceholder: { fontSize: 14, color: ticketColors.ink3, fontFamily: ticketFonts.sans },
  productSelectedRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  productSelectedName: { fontSize: 14, fontWeight: '600', color: ticketColors.ink, flex: 1, fontFamily: ticketFonts.sans },
  pickerSheet: { marginTop: spacing.sm, backgroundColor: 'transparent', borderRadius: 10, padding: spacing.sm, borderWidth: 1, borderColor: ticketColors.ruleSoft },
  pickerList: { maxHeight: 220, marginTop: spacing.xs },
  pickerItem: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: ticketColors.ruleSoft },
  pickerItemName: { fontSize: 13, fontWeight: '500', color: ticketColors.ink, flex: 1, fontFamily: ticketFonts.sans },
  pickerItemPrice: { fontSize: 13, color: ticketColors.ink2, fontFamily: ticketFonts.mono },
  pickerEmpty: { paddingVertical: 16, textAlign: 'center', color: ticketColors.ink3, fontSize: 13, fontFamily: ticketFonts.sans },
});

const earningsTabStyles = StyleSheet.create({
  noPlanHint: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    backgroundColor: ticketColors.paper2,
  },
  noPlanHintText: {
    flex: 1,
    fontSize: 12,
    color: ticketColors.ink2,
    flexWrap: 'wrap',
    fontFamily: ticketFonts.sans,
  },
  noPlanHintLink: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
});
